// apps/share-web/src/song-screen/phone-transport.ts
//
// Транспорт телефонного экрана песни. Решение Сергея 16.10 («единый опыт с
// десктопом»): кнопки — ТЕ ЖЕ, что в десктопе и на компьютере, ⏮ ■ ▶ ● ⏭,
// поэтому это тот же <bw-transport> через player-view/transport.ts (там уже
// загрузка, повтор Play после загрузки, жест iOS для фонового <audio>, REC →
// окно приложения). На телефоне у него скрыты время, бар-счётчик и Master
// (song-screen.css): время — строкой выше, Master — строкой микшера.
// Секции — тапом по волне, начало песни — ■; ‹ › секций нет.
import { mountTransport, type TransportHandle } from '../player-view/transport.js';
import { sectionIndexAt, type SectionSec } from '../chart/chart-follow.js';
import type { Player } from '../player.js';
import type { SongScreenStore } from './song-screen-store.js';
import { t } from '../ui/i18n.js';
import '../styles/song-screen.css';

export interface PhoneTransportDeps {
  player: Player;
  durationSec: number;
  sections: SectionSec[];
  store: SongScreenStore;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onPlayGesture?: () => void;
  onRecord: () => void;
  onClearLoop: () => void;
}

export interface PhoneTransportHandle { sync(posSec: number, playing: boolean): void; destroy(): void; }

const mmss = (sec: number): string => {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export function mountPhoneTransport(host: Element, d: PhoneTransportDeps): PhoneTransportHandle {
  const root = document.createElement('div');
  root.className = 'ss-transport';
  const info = document.createElement('div');
  info.className = 'ss-tr-info';
  const time = document.createElement('span');
  time.className = 'ss-tr-time';
  const sec = document.createElement('span');
  sec.className = 'ss-tr-sec';
  const loop = document.createElement('span');
  loop.className = 'ss-tr-loop';
  loop.hidden = true;
  const loopName = document.createElement('span');
  loop.appendChild(loopName);
  // Своя маленькая кнопка внутри пилюли, как ✕ метки на волне (.pv-loop-tag-off): колпак ДС, растянутый
  // до 44 px, рисовался большим отдельным квадратом рядом с пилюлей. Зона касания 44 px — ::after в CSS.
  const off = document.createElement('button');
  off.type = 'button';
  off.className = 'ss-tr-loop-off';
  off.setAttribute('aria-label', t('wave.loopOff'));
  off.title = t('wave.loopOff');
  off.textContent = '✕';
  off.addEventListener('click', d.onClearLoop);
  loop.appendChild(off);
  info.appendChild(time);
  info.appendChild(sec);
  info.appendChild(loop);
  const btns = document.createElement('div');
  btns.className = 'ss-tr-btns';
  root.appendChild(info);
  root.appendChild(btns);
  host.appendChild(root);

  const tr: TransportHandle = mountTransport(btns, {
    player: d.player,
    durationSec: d.durationSec,
    onRecordAttempt: d.onRecord,
    hasPrev: d.hasPrev,
    hasNext: d.hasNext,
    onPrev: d.onPrev,
    onNext: d.onNext,
    onPlayGesture: d.onPlayGesture,
  });

  const renderLoop = (): void => {
    const id = d.store.get().loopSectionId;
    const s = id ? d.sections.find((x) => x.id === id) : undefined;
    loop.hidden = !s;
    if (s) loopName.textContent = `⟲ ${s.name || t('songScreen.loop')}`;
  };
  const unsub = d.store.subscribe((s, prev) => { if (s.loopSectionId !== prev.loopSectionId) renderLoop(); });
  renderLoop();

  let lastTime = '';
  let lastIdx = -2;
  let lastPlaying: boolean | null = null;
  return {
    sync(posSec, playing) {
      const txt = `${mmss(posSec)} / ${mmss(d.durationSec)}`;
      if (txt !== lastTime) { lastTime = txt; time.textContent = txt; }
      const idx = sectionIndexAt(d.sections, posSec);
      // Безымянная секция — тире, а не пустое место между временем и меткой петли.
      if (idx !== lastIdx) { lastIdx = idx; sec.textContent = idx >= 0 ? (d.sections[idx].name || '—') : ''; }
      // На телефоне у <bw-transport> видны только кнопки (время и таймлайн скрыты в CSS),
      // а syncTime перерисовывает компонент целиком — на 60 Гц это лишний рендер Lit.
      // Нужно лишь состояние Play: толкаем при смене и на первом кадре.
      if (playing !== lastPlaying) { lastPlaying = playing; tr.syncTime(posSec, playing, 0); }
    },
    destroy() { unsub(); off.removeEventListener('click', d.onClearLoop); tr.unmount(); root.remove(); },
  };
}
